import type {
  DownPaymentComparison,
  EstimateRequest,
  Percentage,
} from "./agent-types";
import { ApiError, apiRequest } from "./api";
import { isAbortError } from "./estimates";

export type DownPaymentComparisonPreviewRequest = {
  estimate: EstimateRequest;
  down_payment_percents?: Percentage[];
};

export type DownPaymentComparisonPreviewResponse = {
  comparison: DownPaymentComparison;
};

export class DownPaymentComparisonPreviewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DownPaymentComparisonPreviewError";
  }
}

export class DownPaymentComparisonUnauthorizedError extends DownPaymentComparisonPreviewError {
  constructor(message = "Please sign in again to refresh down-payment comparison.") {
    super(message);
    this.name = "DownPaymentComparisonUnauthorizedError";
  }
}

export class DownPaymentComparisonValidationError extends DownPaymentComparisonPreviewError {
  constructor(public details: string) {
    super(details || "validation failed");
    this.name = "DownPaymentComparisonValidationError";
  }
}

export class DownPaymentComparisonServiceUnavailableError extends DownPaymentComparisonPreviewError {
  constructor() {
    super("down-payment comparison service unavailable");
    this.name = "DownPaymentComparisonServiceUnavailableError";
  }
}

export class DownPaymentComparisonNetworkError extends DownPaymentComparisonPreviewError {
  constructor(public cause: unknown) {
    super("network error");
    this.name = "DownPaymentComparisonNetworkError";
  }
}

export type FetchDownPaymentComparisonOptions = {
  token: string;
  signal?: AbortSignal;
};

export async function fetchDownPaymentComparisonPreview(
  req: DownPaymentComparisonPreviewRequest,
  opts: FetchDownPaymentComparisonOptions,
): Promise<DownPaymentComparisonPreviewResponse> {
  try {
    return await apiRequest<DownPaymentComparisonPreviewResponse>(
      "/api/down-payment-comparison/preview",
      {
        method: "POST",
        body: req,
        token: opts.token,
        signal: opts.signal,
      },
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        throw new DownPaymentComparisonUnauthorizedError(err.message);
      }
      if (err.status === 422 || err.status === 400) {
        throw new DownPaymentComparisonValidationError(err.message);
      }
      if (err.status === 503) {
        throw new DownPaymentComparisonServiceUnavailableError();
      }
      throw new DownPaymentComparisonPreviewError(err.message);
    }
    throw new DownPaymentComparisonNetworkError(err);
  }
}
